import React from 'react';
import styled, { css } from 'styled-components';

import colors from '@front/common/src/utils/colors';
import { useModalObject } from '@front/common/src/hooks/use-modal';
import { getCurrentCountry } from '@front/common/src/utils/country';

import { getFormattedPrice, getFormattedPromotion } from '@front/customer/src/utils/cleaning';
import { MemberPrices, StandardPrices } from '@front/customer/src/types/prices';
import { CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';

import { translate } from '@front/i18n';

import Text from '@front/web-ui/src/components/base-text/variants/text';
import Separator from '@front/web-ui/src/components/separator/separator';
import TextButton from '@front/web-ui/src/components/button/text-button';
import Skeleton from '@front/web-ui/src/components/skeleton/skeleton';

import ServiceFeeIncludedText from '../../service-fee-included-text';
import TaxModal from '../common/tax-modal';
import Reduction from '../common/reduction';

const t = translate('basket');

const ReductionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 5px;
  padding: 10px 0;
`;

const calculationLineStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: 10px 0;
  white-space: nowrap;
`;

const PromotionLine = styled(Reduction)`
  ${calculationLineStyle}
  padding: 0;
`;

const CalculationLine = styled.div`
  ${calculationLineStyle}
`;

type SinglePricesWithTaxProps = {
  hasDuration: boolean;
  hasPrestations: boolean;
  isLoading: boolean;
  prices?: StandardPrices | MemberPrices;
};

const SinglePricesWithTax = ({ hasDuration, hasPrestations, isLoading, prices }: SinglePricesWithTaxProps) => {
  const { isOpen, open, close } = useModalObject();
  const { hasTaxCreditInBasket } = CONFIG_BY_COUNTRY[getCurrentCountry()];

  const hasPromo = !!prices?.promotion;
  // If the prices has a discount value, it means that the user has the membership
  const shouldShowMembershipReduction = !!prices && 'discount' in prices && prices.discount.price > 0;
  const hasAnyReduction = shouldShowMembershipReduction || hasPromo;

  return (
    <>
      <TaxModal onClose={close} isOpen={isOpen} />
      {hasAnyReduction && (
        <>
          <Separator />
          <ReductionsWrapper>
            {shouldShowMembershipReduction && (
              <Reduction
                title={t('.membership.reductions')}
                reduction={getFormattedPrice(hasDuration, prices.discount)}
                label={t('.membership.label_full')}
              />
            )}
            {hasPromo && (
              <PromotionLine
                title={t('.promo_code')}
                reduction={getFormattedPromotion(hasDuration, prices?.promotion)}
              />
            )}
          </ReductionsWrapper>
        </>
      )}
      {hasPrestations && (
        <>
          <Separator />
          <CalculationLine>
            <Text color={colors.ink100}>{t('.total')}</Text>
            {isLoading ? (
              <Skeleton linesWidth={['50px']} />
            ) : (
              <Text color={colors.ink100}>{getFormattedPrice(hasDuration, prices?.total)}</Text>
            )}
          </CalculationLine>
          {hasTaxCreditInBasket && (
            <>
              <Separator />
              <CalculationLine>
                <TextButton onClick={open} label={t('.cleaning.services.tax.label')}>
                  {t('.cleaning.services.tax.label')}
                </TextButton>
                {isLoading ? (
                  <Skeleton linesWidth={['50px']} />
                ) : (
                  <Text color={colors.ink100}>{`- ${getFormattedPrice(hasDuration, prices?.tax_credit)}`}</Text>
                )}
              </CalculationLine>
              <Separator />
              <CalculationLine>
                <Text.MDBold>{t('.after_tax_credit')}</Text.MDBold>
                {isLoading ? (
                  <Skeleton linesWidth={['50px']} />
                ) : (
                  <Text.MDBold color={colors.ink100}>{getFormattedPrice(hasDuration, prices?.after_tax)}</Text.MDBold>
                )}
              </CalculationLine>
            </>
          )}
        </>
      )}

      <ServiceFeeIncludedText />
    </>
  );
};

export default SinglePricesWithTax;
